import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UtilityService } from '../../../core/services/utility.service';
import { ActionAfterModalClose } from '../../../shared/models/utility.model';
import { IdentityVerificationStatus, Countdown } from '../../model/kamonal-model';
import { StringHelper } from '../../../core/helpers/strings.helper';

@Component({
    selector: 'app-kamona-id-verification',
    templateUrl: './kamona-id-verification.component.html',
    styleUrls: ['./kamona-id-verification.component.scss'],
})
export class KamonaIdVerificationComponent implements OnInit, OnDestroy {
    readonly TOTAL_COUNTDOWN_IN_MINUTE = 9;
    countdown: Countdown;
    readonly verificationDelayTimeInMilliseconds = 1000;
    totalVerificationTimeSpentInMilliseconds = 0;
    interval: any;
    countdownInterval: any;
    readonly TOTAL_VERIFICATION_TIME_IN_MILLISECONDS = 540000;
    canShowContinueButton = false;
    actionAfterModalClose: ActionAfterModalClose;
    readonly TOTAL_TIME_IN_SEC = this.TOTAL_COUNTDOWN_IN_MINUTE * 60;
    verificationMessage: string;
    verified: boolean;
    readonly PERCENTAGE = 100;
    readonly SECONDS_CONVERTER = 60;
    @Input() verificationUrl: string;
    @Input() verificationPayload: any;
    @Input() httpMethod = 'POST';
    progressStyle: any;
    @Output() emitVerifiedIdentity = new EventEmitter();
    @Output() emitFailedIdentity = new EventEmitter();
    readonly VERIFICATION_TIMEOUT = 'Verification Timeout. Please try again';
    reference: string;
    constructor(private utility: UtilityService) {}

    ngOnInit(): void {}

    ngOnDestroy() {
        this.clearRunningInterval();
    }

    initialize() {
        this.countdown = undefined;
        this.checkVerificationStatus();
    }

    setStrokeDashoffset(offset: number) {
        this.progressStyle = {
            'stroke-dashoffset': offset,
        };
    }

    showModal() {
        const modal: any = document.querySelector('[id-verification-modal]');
        modal?.showModal();
        modal.addEventListener('cancel', (event) => {
            event.preventDefault();
        });
    }

    getOffset() {
        const percentageTime = this.getCurrentTimeInSec() / this.TOTAL_TIME_IN_SEC;
        return percentageTime * this.PERCENTAGE;
    }

    getCurrentTimeInSec() {
        const seconds = this.countdown?.second;
        const minutes = this.countdown?.minute;
        const minInSec = minutes * this.SECONDS_CONVERTER;
        const totalTimeInSec = seconds + minInSec;
        return totalTimeInSec;
    }

    checkVerificationStatus = () => {
        const isPostRequest = this.httpMethod === 'POST';
        isPostRequest ? this.handleVerificationPostRequest() : this.handleVerificationGetRequest();
    };

    handleVerificationPostRequest() {
        this.utility
            .getIdentityVerificationStatus(this.verificationUrl, this.verificationPayload)
            .then((res) => {
                this.handleIdentityVerificationStatusResponse(res);
            })
            .catch((error) => {
                this.handleIdentityVerificationStatusFailure(error);
            });
    }

    handleVerificationGetRequest() {
        this.utility
            .getAPIIdentityVerificationStatus(this.verificationUrl)
            .then((res) => {
                this.handleIdentityVerificationStatusResponse(res);
            })
            .catch((error) => {
                this.handleIdentityVerificationStatusFailure(error);
            });
    }

    handleIdentityVerificationStatusResponse(res: any) {
        if (this.isFailedVerification(res?.status)) {
            return this.handleVerificationFailed();
        }
        this.reference = res?.reference;
        this.verificationMessage = res?.message;
        const status = this.getFormattedStatus(res?.status);
        const verified = this.isVerified(status);
        verified ? this.handleVerificationSuccess() : this.continueVerificationIfNotVerified();
    }

    getFormattedStatus(status: string) {
        if (!status) {
            return IdentityVerificationStatus.PENDING;
        }
        return IdentityVerificationStatus[status.toUpperCase()];
    }

    continueVerificationIfNotVerified() {
        this.hasExceededVerificationTime() ? this.handleVerificationTimeout() : this.continueVerification();
    }

    isVerified(status: IdentityVerificationStatus) {
        return status === IdentityVerificationStatus.SUCCESS;
    }

    isFailedVerification(status: IdentityVerificationStatus) {
        return status === IdentityVerificationStatus.FAILED;
    }

    handleIdentityVerificationStatusFailure(error: any) {
        this.verificationMessage = StringHelper.getErrorMessageHttpErrorResponse(error);
        this.handleVerificationFailed();
    }

    handleVerificationSuccess = () => {
        this.clearRunningInterval();
        const params = { reference: this.reference, message: this.verificationMessage };
        this.emitVerifiedIdentity.emit(params);
    };

    handleVerificationFailed = () => {
        this.clearRunningInterval();
        const message = this.verificationMessage ?? 'Verification Failed';
        const params = { message, shouldGetNewReference: true };
        this.emitFailedIdentity.emit(params);
    };

    clearRunningInterval() {
        clearInterval(this.interval);
        this.clearCountdownInterval();
    }

    clearCountdownInterval() {
        clearInterval(this.countdownInterval);
    }

    continueVerification() {
        if (!this.countdown) this.startCountdown();
        this.interval = setTimeout(() => {
            this.handleOnInterval();
        }, this.geDelayTime());
    }

    startCountdown() {
        let time = this.TOTAL_COUNTDOWN_IN_MINUTE * 60;
        this.countdownInterval = setInterval(() => {
            this.updateCountdown(time);
            time--;
            this.stopCountdown();
        }, 1000);
    }

    stopCountdown() {
        const isMinuteTimeout = this.countdown?.minute <= 0;
        const isSecondTimeout = this.countdown?.second <= 0;
        if (isMinuteTimeout && isSecondTimeout) this.clearCountdownInterval();
    }

    handleOnInterval() {
        this.updateTotalVerificationTimeSpentInMilliseconds();
        this.setStrokeDashoffset(this.getOffset());
        this.checkVerificationStatus();
    }

    updateCountdown(time: number) {
        const minute = this.getMinutes(time);
        const second = this.getSeconds(time);
        this.countdown = { minute, second };
    }

    handleVerificationTimeout() {
        this.verificationMessage = this.VERIFICATION_TIMEOUT;
        this.handleVerificationFailed();
    }

    getSeconds(time: number) {
        return Math.floor(time % 60);
    }

    getMinutes(time: number) {
        return Math.floor(time / 60);
    }

    geDelayTime() {
        const random = StringHelper.getRandomInteger(16, 25);
        return this.verificationDelayTimeInMilliseconds * random;
    }

    updateTotalVerificationTimeSpentInMilliseconds() {
        this.totalVerificationTimeSpentInMilliseconds = this.totalVerificationTimeSpentInMilliseconds + this.verificationDelayTimeInMilliseconds;
    }

    hasExceededVerificationTime() {
        return this.totalVerificationTimeSpentInMilliseconds >= this.TOTAL_VERIFICATION_TIME_IN_MILLISECONDS;
    }
}
